import { Show, Switch, Match, For, Suspense, createMemo } from 'solid-js';
import { Button, Tag, Link, Heading, HorizontalRule } from '@troon/ui';
import { IconCalendar } from '@troon/icons/calendar';
import { IconTag } from '@troon/icons/tag';
import { IconUserCircle } from '@troon/icons/user-circle';
import { IconBellRing } from '@troon/icons/bell-ring';
import { IconCreditCard01 } from '@troon/icons/credit-card-01';
import { IconLogOut } from '@troon/icons/log-out';
import { NavigationMenu as KNavigationMenu } from '@kobalte/core/navigation-menu';
import { createAsync } from '@solidjs/router';
import { Dynamic } from 'solid-js/web';
import { IconGift } from '@troon/icons/gift';
import { cachedQuery } from '../graphql/cached-get';
import { gql } from '../graphql';
import { AccessPremiumTag } from './access-premium-tag';
import type { ParentProps } from 'solid-js';
import type { IconComponent } from '@troon/icons';
import type { UserData } from '../providers/user';

type NavItem = {
	icon: IconComponent;
	href: string;
	children: string;
	condition?: (data: UserData) => boolean;
	notificationCount?: number;
};

const userNav: Array<NavItem> = [
	{
		icon: IconGift,
		href: '/membership/benefits',
		children: 'Offers & Benefits',
		condition: (data) => !!data.troonAccessProductType,
	},
	{
		icon: IconTag,
		href: '/account/subscription',
		children: 'Manage Membership',
		condition: (data) => !!data.troonAccessProductType,
	},
	{ icon: IconUserCircle, href: '/account/profile', children: 'Edit Profile Details' },
	{ icon: IconBellRing, href: '/alerts', children: 'Tee Time Alerts' },
	{ icon: IconCreditCard01, href: '/account/payment-methods', children: 'Payment Methods' },
];

export function AccountDropdown(props: UserData) {
	const reservations = createAsync(() => getReservations({}));
	const rewardsLevel = createMemo(() =>
		props.accessRewardsOptIn
			? (props.accessRewardsBalance?.tierOverride ?? props.accessRewardsBalance?.currentTier)
			: props.rewardsLevel,
	);

	return (
		<>
			<KNavigationMenu.Item class="flex min-w-96 flex-col gap-4 px-4 pt-4 pb-2 outline-none">
				<div class="flex flex-row items-center gap-2 text-black">
					<Show when={props.troonAccessProductType}>
						{(productType) => <AccessPremiumTag product={productType()} />}
					</Show>
					<Switch>
						<Match when={rewardsLevel() === 'SILVER'}>
							<Tag appearance="silver">Silver</Tag>
						</Match>
						<Match when={rewardsLevel() === 'GOLD'}>
							<Tag appearance="gold">Gold</Tag>
						</Match>
						<Match when={rewardsLevel() === 'PLATINUM'}>
							<Tag appearance="platinum">Platinum</Tag>
						</Match>
					</Switch>
				</div>
				<div class="flex flex-col gap-2">
					<Heading as="h2" size="h5" class="text-black">
						{props.firstName} {props.lastName}
					</Heading>
					<p class="text-neutral-800">
						#{props.troonRewardsId} •{' '}
						{props.accessRewardsOptIn && props.accessRewardsBalance
							? props.accessRewardsBalance.availablePoints.toLocaleString()
							: props.rewardsPoints?.toLocaleString()}{' '}
						PTS
					</p>
				</div>
				<Button as={Link} href="/account" appearance="tertiary-current" class="w-full px-3 py-2.5 text-brand">
					View Account
				</Button>
			</KNavigationMenu.Item>

			<HorizontalRule />

			<KNavigationMenu.Group class="flex flex-col gap-2 pt-2">
				<KNavigationMenu.GroupLabel class="px-4 text-sm text-neutral-800 uppercase">Account</KNavigationMenu.GroupLabel>
				<Suspense
					fallback={
						<NavItem href="/account/reservations" icon={IconCalendar}>
							Reservations
						</NavItem>
					}
				>
					<NavItem
						href="/account/reservations"
						icon={IconCalendar}
						notificationCount={reservations()?.reservations?.length || 0}
					>
						Reservations
					</NavItem>
				</Suspense>
				<For each={userNav}>
					{(item) => (
						<Show when={!item.condition || item.condition(props)}>
							<NavItem href={item.href} icon={item.icon}>
								{item.children}
							</NavItem>
						</Show>
					)}
				</For>
			</KNavigationMenu.Group>

			<HorizontalRule />

			<KNavigationMenu.Item
				href="/auth/logout"
				class="group relative mx-2 mb-2 flex h-10 flex-row items-center justify-center gap-2 overflow-hidden rounded bg-white px-2 text-neutral-900 transition-colors duration-200 outline-none hover:bg-brand-100 hover:text-brand-600"
			>
				<IconLogOut />
				Log Out
			</KNavigationMenu.Item>
		</>
	);
}

type NavItemProps = ParentProps<{
	href: string;
	icon: IconComponent;
	notificationCount?: number;
}>;

function NavItem(props: NavItemProps) {
	return (
		<KNavigationMenu.Item
			href={props.href}
			class="relative mx-2 flex h-10 flex-row items-center gap-2 overflow-hidden rounded bg-white px-2 text-neutral-900 transition-colors duration-200 outline-none hover:bg-brand-100 hover:text-brand-600"
		>
			<Dynamic component={props.icon} />
			{props.children}
			<Show when={props.notificationCount}>
				<span class="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">
					{props.notificationCount}
				</span>
			</Show>
		</KNavigationMenu.Item>
	);
}

const reservationsQuery = gql(`
query accountReservationsCount {
	reservations: allReservations(upcoming: true, past: false) { id }
}`);

const getReservations = cachedQuery(reservationsQuery);
